import { DownOutlined } from '@ant-design/icons'
import type { ApolloClient } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Button, Dropdown } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  SourceInterval,
  UpdateSourceDurationDocument,
  type UpdateSourceDurationMutation,
} from '~app/graph/generated/graphql'
import { useUpdatingContext } from '../useUpdatingContext'

/*
 * Hooks
 */

const useDuration = (
  sourceId: string
): {
  updateDuration: (
    newDuration: SourceInterval
  ) => Promise<ApolloClient.MutateResult<UpdateSourceDurationMutation>>
  loading: boolean
} => {
  const [updateSourceDurationMutation, { loading }] = useMutation(
    UpdateSourceDurationDocument
  )
  const { setUpdating } = useUpdatingContext()
  return {
    updateDuration: newDuration => {
      setUpdating(sourceId, true)
      return updateSourceDurationMutation({
        variables: { id: sourceId, duration: newDuration },
      }).finally(() => setUpdating(sourceId, false))
    },
    loading,
  }
}

/*
 * Component
 */

interface Props {
  sourceId: string
  duration: SourceInterval | null
}

// TODO: note that when duration is 'null', we display 12 months. Is this correct?
const Duration = ({ sourceId, duration }: Props) => {
  const { updateDuration, loading } = useDuration(sourceId)
  const { t } = useTranslation()

  const items =
    duration === SourceInterval.HalfYearly
      ? [
          {
            key: '0',
            label: `12 ${t('misc.months')}`,
            onClick: () => updateDuration(SourceInterval.Yearly),
          },
        ]
      : [
          {
            key: '1',
            label: `6 ${t('misc.months')}`,
            onClick: () => updateDuration(SourceInterval.HalfYearly),
          },
        ]

  return (
    <Dropdown trigger={['click']} menu={{ items }}>
      <Button style={{ whiteSpace: 'pre' }} loading={loading}>
        {duration === SourceInterval.HalfYearly ? '6' : '12'} {t('misc.months')}
        <DownOutlined />
      </Button>
    </Dropdown>
  )
}

export default Duration
